import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { DialogWrapperComponent } from '../../../../../shared/components/wrappers/dialog-wrapper/dialog-wrapper.component';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { NgClass, UpperCasePipe } from '@angular/common';
import {
  ButtonSize,
  ButtonStyle
} from '../../../../../shared/enums/button.enum';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TruncatePipe } from '../../../../../shared/pipes/truncate.pipe';
import { openGoogleMapsLocation } from '../../../../../shared/utils/google-maps.util';
import {
  RestaurantGeneralState,
  RestaurantState
} from '../../../../../shared/state/restaurant/restaurant.state';
import { PhonePipe } from '../../../../../shared/pipes/phone.pipe';
import {
  WooCommerceRestaurantHolidays,
  WooCommerceRestaurantHoursValue
} from '../../../../../core/interfaces/woo-commerce/api/restaurant/restaurant-api.interface';
import { select, Store } from '@ngxs/store';
import { getHolidayNames } from '../../../../../shared/utils/holidays.utils';

@Component({
  selector: 'app-restaurant-details-dialog',
  imports: [
    DialogWrapperComponent,
    ButtonComponent,
    NgClass,
    TruncatePipe,
    UpperCasePipe,
    PhonePipe
  ],
  templateUrl: './restaurant-details-dialog.component.html',
  styleUrl: './restaurant-details-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RestaurantDetailsDialogComponent implements OnInit {
  restaurant: RestaurantGeneralState = inject(DIALOG_DATA).restaurant;
  workingHours: WooCommerceRestaurantHoursValue[] =
    inject(DIALOG_DATA).workingHours;
  aboutUs: string = this.restaurant.aboutUs || this.restaurant.description || '';

  descriptionExpanded: WritableSignal<boolean> = signal(false);
  workingHoursExpanded: WritableSignal<boolean> = signal(false);
  currentWeekday: WritableSignal<string> = signal<string>('');

  isRestaurantOpen: Signal<boolean> = select(RestaurantState.isOpen);
  isTodayHoliday: Signal<boolean> = select(RestaurantState.isTodayHoliday);
  holidays: Signal<WooCommerceRestaurantHolidays> = select(
    RestaurantState.holidays
  );
  timezone: Signal<string | undefined> = select(RestaurantState.timezone);

  // Get today's holiday reason (if any)
  todayHolidayReason: WritableSignal<string | undefined> = signal(undefined);

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonStyle = ButtonStyle;

  constructor(
    protected dialogRef: DialogRef,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.setCurrentWeekday();
    this.updateTodayHolidayReason();
  }

  private updateTodayHolidayReason(): void {
    const holidays = this.store.selectSnapshot(RestaurantState.holidays);
    const timezone = this.store.selectSnapshot(RestaurantState.timezone);
    const reasons = getHolidayNames(new Date(), holidays, timezone);
    if (reasons && reasons.length > 0) {
      // Capitalize first letter of each reason
      this.todayHolidayReason.set(
        reasons.map((r) => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')
      );
    }
  }

  setCurrentWeekday(): void {
    const weekday = new Date().toLocaleDateString('en-US', { weekday: 'long' });
    this.currentWeekday.set(weekday);
  }

  toggleDescriptionExpanded(): void {
    this.descriptionExpanded.set(!this.descriptionExpanded());
  }

  toggleWorkingHoursExpanded(): void {
    this.workingHoursExpanded.set(!this.workingHoursExpanded());
  }

  initPhoneCall(phone: string): void {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  }

  openGoogleMapsLocation(address: string): void {
    openGoogleMapsLocation(address);
  }
}
